// The tasks board (Kanban; route #/taches, key T): a full-screen sheet with a black header, a
// toolbar (floor, service chips, period, counts) and seven columns — À faire, En cours, À valider,
// Bloquées, En erreur, Terminées, Ignorées — of cards (title, service colour, agent, time, attempts,
// error or decision line; the runs of a routine share one card). A card is dragged to another
// column, or moved with its ⋯ menu (keyboard, touch): retry, ignore, cancel, stop ignoring, mark
// settled. A click opens the task drawer, which comes back here when closed. Live: a `task` event
// reloads the board when a card may change column. On a phone the columns scroll sideways one at a
// time, under a row of column chips.
import { h, icon, button, chip, segmented, emptyState, spinner, setText } from '../dom.js';
import { t } from '../i18n/fr.js';
import { deptColor } from '../format.js';
import { sortedFloors, floorDepartments, can } from '../store.js';
import {
  COLUMNS, PERIODS, DEFAULT_DAYS, columnLabel, movesFor, moveTo, moveLabel, moveRequest, undoOf, withCardMoved, cardTime,
  cardBadges, cardLine, isRelevant, cardSignature, boardSummary, boardParams,
} from './kanban/model.js';
import { childLayers, isMobile, storageGet, storageSet } from './automations/widgets.js';
import './kanban/strings.js';

const DAYS_KEY = 'mo.kb.days';
const RELOAD_DEBOUNCE_MS = 600;
const MOVE_ICONS = { retry: 'refresh', ignore: 'eyeOff', unignore: 'eye', cancel: 'close', settle: 'check' };

/**
 * The scope the board opens on: the floor on screen (every floor from the building view) and the
 * department the task panel is filtered on.
 * @param {object} state app state
 * @returns {{floorId: string, deptId: string}}
 */
export function defaultScope(state) {
  const floors = state?.office?.floors || [];
  const floorId = state?.ui?.view === 'building' ? '' : state?.ui?.floorId || '';
  const scoped = floorId && floors.some((f) => f.id === floorId) ? floorId : '';
  const dept = (state?.office?.departments || []).find((d) => d.id === state?.ui?.deptFilter);
  return { floorId: scoped, deptId: dept && (!scoped || dept.floorId === scoped) ? dept.id : '' };
}

/**
 * Opens the board.
 * @param {object} ctx app context
 * @returns {import('./overlay.js').Layer}
 */
export function openKanban(ctx) {
  const now = () => Date.now() + (Number(ctx.store.get().serverOffset) || 0);
  const savedDays = Number(storageGet(DAYS_KEY));
  const st = {
    ...defaultScope(ctx.store.get()),
    days: PERIODS.includes(savedDays) ? savedDays : DEFAULT_DAYS,
    expand: new Set(),
    board: null,
    status: 'loading',
    error: '',
    mobile: isMobile(),
    activeColumn: 'todo',
  };
  let layer = null;
  let children = null;
  let loadSeq = 0;
  let reloadTimer = null;
  let minuteTimer = null;
  let drag = null;
  const busy = new Set();
  const offs = [];

  // --- skeleton ---------------------------------------------------------------------------------
  const periodSeg = segmented(PERIODS.map((d) => ({ value: String(d), label: t(`kb.period.${d}`) })), {
    value: String(st.days), size: 'sm', label: t('kb.period'),
    onChange: (v) => { st.days = Number(v); storageSet(DAYS_KEY, v); load(); },
  });
  periodSeg.classList.add('kb-periodseg');
  const headHint = h('p', { class: 'kb-headhint', 'aria-hidden': 'true' }, t('kb.hint'));
  const headerActions = h('div', { class: 'kb-headbar' }, periodSeg, headHint);

  const floorSelect = h('select', { class: 'input select input-sm kb-floor', 'aria-label': t('kb.floor') });
  floorSelect.addEventListener('change', () => {
    st.floorId = floorSelect.value;
    if (st.deptId && !departments().some((d) => d.id === st.deptId)) st.deptId = '';
    st.expand.clear();
    load();
  });
  const chipsBox = h('div', { class: 'kb-chips', role: 'group', 'aria-label': t('kb.depts') });
  const summaryBox = h('p', { class: 'kb-summary', 'aria-live': 'polite', title: '' });
  const toolbar = h('div', { class: 'kb-toolbar' }, floorSelect, chipsBox, summaryBox);
  const colNav = h('div', { class: 'kb-colnav', role: 'group', 'aria-label': t('kb.columnsNav') });
  const notice = h('div', { class: 'kb-notice', hidden: true });
  const boardEl = h('div', { class: 'kb-board', role: 'region', 'aria-label': t('kb.boardLabel') });
  const root = h('div', { class: 'kb-root' }, toolbar, colNav, notice, boardEl);

  // --- helpers ----------------------------------------------------------------------------------
  const office = () => ctx.store.get().office || {};
  const canOperate = (card) => can(ctx.store.get(), 'operate', { floorId: card.floorId, deptId: card.deptId });
  const columnOf = (id) => st.board?.columns?.find((c) => c.id === id) || null;

  function departments() {
    const off = office();
    const out = [];
    for (const f of sortedFloors(off.floors || [])) {
      if (st.floorId && f.id !== st.floorId) continue;
      for (const d of floorDepartments(off.departments || [], f.id)) out.push({ ...d, floorName: f.name });
    }
    return out;
  }

  /** card id → the signature of the task it shows (for the live events). */
  function knownCards() {
    const map = new Map();
    for (const c of st.board?.columns || []) for (const card of c.cards) map.set(card.id, cardSignature(card));
    return map;
  }

  // --- rendering --------------------------------------------------------------------------------
  function renderToolbar() {
    const off = office();
    const floors = sortedFloors(off.floors || []);
    const uiFloor = ctx.store.get().ui?.floorId;
    floorSelect.replaceChildren(h('option', { value: '' }, t('kb.floorAll')),
      ...floors.map((f) => h('option', { value: f.id }, f.id === uiFloor ? t('kb.floorCurrent', { name: f.name }) : f.name)));
    floorSelect.value = st.floorId;
    floorSelect.hidden = floors.length < 2;
    const depts = departments();
    const all = chip(t('kb.deptAll'), { active: !st.deptId, onClick: () => setDept('') });
    all.dataset.fk = 'dept:';
    chipsBox.replaceChildren(all, ...depts.map((d) => {
      const c = chip(d.name, { active: st.deptId === d.id, dot: deptColor(d.color, d.id), onClick: () => setDept(st.deptId === d.id ? '' : d.id) });
      c.dataset.fk = `dept:${d.id}`;
      if (!st.floorId) c.setAttribute('title', `${d.floorName} · ${d.name}`);
      return c;
    }));
    chipsBox.hidden = depts.length < 2;
    const sum = boardSummary(st.board);
    summaryBox.replaceChildren(
      h('span', { class: 'kb-sum' }, h('b', { class: 'kb-sum-n' }, String(sum.tasks)), ' ', h('span', { class: 'mono-label' }, t('kb.summary', { n: sum.tasks }).replace(/^\d+\s/, ''))),
      h('span', { class: ['kb-sum', { 'is-error': sum.errors > 0 }] },
        sum.errors ? h('b', { class: 'kb-sum-n' }, String(sum.errors)) : null, sum.errors ? ' ' : null,
        h('span', { class: 'mono-label' }, sum.errors ? t('kb.summaryErrors', { n: sum.errors }).replace(/^\d+\s/, '') : t('kb.summaryNone'))));
    summaryBox.setAttribute('title', t('kb.periodHint', { days: st.days }));
  }

  function renderNav() {
    colNav.hidden = !st.mobile;
    if (!st.mobile) { colNav.replaceChildren(); return; }
    let active = null;
    colNav.replaceChildren(...COLUMNS.map((c) => {
      const n = columnOf(c.id)?.taskCount ?? 0;
      const b = chip(columnLabel(c.id), { active: st.activeColumn === c.id, count: n, onClick: () => scrollToColumn(c.id) });
      b.classList.add(`kb-navchip`, `is-${c.tone}`);
      if (st.activeColumn === c.id) active = b;
      return b;
    }));
    // The chip of the column in view stays visible in its row.
    if (active && colNav.scrollWidth > colNav.clientWidth) {
      colNav.scrollLeft = Math.max(0, active.offsetLeft - (colNav.clientWidth - active.offsetWidth) / 2);
    }
  }

  function renderNotice() {
    const rows = [];
    if (st.status === 'error') {
      rows.push(h('div', { class: 'kb-notice-row is-error', role: 'alert' }, h('span', {}, st.error || t('kb.error')),
        button(t('kb.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', onClick: () => load() })));
    }
    notice.replaceChildren(...rows);
    notice.hidden = !rows.length;
  }

  function renderBoard() {
    if (st.status === 'unavailable') {
      boardEl.replaceChildren(h('div', { class: 'kb-message' }, emptyState({ icon: 'alert', title: t('kb.unavailable') })));
      return;
    }
    if (!st.board) {
      boardEl.replaceChildren(h('div', { class: 'kb-message' }, spinner(t('kb.loading')), h('p', { class: 'muted' }, t('kb.loading'))));
      return;
    }
    const scroll = boardEl.scrollLeft;
    boardEl.replaceChildren(...COLUMNS.map((c) => columnView(c, columnOf(c.id))));
    boardEl.scrollLeft = scroll;
  }

  function columnView(def, col) {
    const id = def.id;
    const cards = col?.cards || [];
    const count = col?.taskCount ?? 0;
    const titleId = `kb-col-${id}`;
    const list = h('ul', { class: 'kb-list', 'aria-labelledby': titleId }, cards.map((card) => h('li', { class: 'kb-item' }, cardView(card))));
    const more = col?.more > 0
      ? button(t('kb.more', { n: col.more }), {
        variant: 'ghost', size: 'sm', class: 'kb-more', ariaLabel: t('kb.moreAria', { column: columnLabel(id) }),
        onClick: () => { st.expand.add(id); load(); },
      })
      : null;
    const section = h('section', {
      class: ['kb-col', `is-${def.tone}`], dataset: { column: id }, 'aria-labelledby': titleId,
    },
    h('header', { class: 'kb-col-head' },
      h('span', { class: 'kb-col-dot', 'aria-hidden': 'true' }),
      h('h3', { class: 'kb-col-title', id: titleId }, columnLabel(id)),
      h('span', { class: ['kb-col-count', { 'is-zero': !count }], title: t('kb.colAria', { column: columnLabel(id), n: count }) }, String(count))),
    h('p', { class: 'kb-col-hint' }, t(`kb.hint.${id}`)),
    h('div', { class: 'kb-col-body' },
      cards.length ? list : h('p', { class: 'kb-col-empty' }, t(`kb.empty.${id}`)),
      more,
      h('p', { class: 'kb-drop', 'aria-hidden': 'true' }, t('kb.dropHere'))));
    section.addEventListener('dragover', (e) => {
      if (!drag || !moveTo(drag.card, id, { canOperate: drag.canOperate })) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      section.classList.add('is-over');
    });
    section.addEventListener('dragleave', (e) => {
      if (!section.contains?.(e.relatedTarget)) section.classList.remove('is-over');
    });
    section.addEventListener('drop', (e) => {
      e.preventDefault();
      const d = drag;
      endDrag();
      if (!d) return;
      const move = moveTo(d.card, id, { canOperate: d.canOperate });
      if (move) perform(d.card, move);
      else if (d.card.column !== id) ctx.toast.info(t(d.canOperate ? 'kb.move.notAllowed' : 'kb.move.noRight', { column: columnLabel(id) }));
    });
    return section;
  }

  function cardView(card) {
    const off = office();
    const dept = (off.departments || []).find((d) => d.id === card.deptId);
    const agent = (off.agents || []).find((a) => a.id === card.agentId);
    const title = card.title || t('kb.card.untitled');
    const column = columnLabel(card.column);
    const ops = canOperate(card);
    const moves = movesFor(card, { canOperate: ops });
    const line = cardLine(card);
    const badges = cardBadges(card, now());
    const routine = card.source?.kind === 'routine'
      ? (card.routineTitle ? t('kb.card.routine', { title: card.routineTitle }) : t('kb.card.routineAnon')) : '';
    const open = h('button', {
      type: 'button', class: 'kb-card-open', dataset: { fk: `card:${card.id}` },
      'aria-label': t('kb.card.open', { title, column }), onClick: () => openTask(card),
    }, title);
    const menuBtn = h('button', {
      type: 'button', class: 'kb-card-menu', 'aria-haspopup': 'menu', 'aria-label': t('kb.card.menu', { title }),
      title: t('kb.menu.title'), dataset: { fk: `menu:${card.id}` },
      onClick: () => openMenu(card, menuBtn),
    }, icon('dots', { size: 16 }));
    const el = h('article', {
      class: ['kb-card', { 'is-pending': !!card.pending || busy.has(card.id), 'is-draggable': moves.length > 0 }],
      dataset: { id: card.id, column: card.column, state: card.state },
      style: { '--dept': deptColor(dept?.color, card.deptId || '') },
      draggable: moves.length ? 'true' : null,
    },
    h('div', { class: 'kb-card-top' },
      h('span', { class: 'kb-card-dept' }, h('span', { class: 'kb-card-dot', 'aria-hidden': 'true' }), card.deptName || dept?.name || '')),
    h('p', { class: 'kb-card-title' }, open),
    h('p', { class: 'kb-card-meta' },
      h('span', { class: 'kb-card-agent' }, card.agentName || agent?.name || t('kb.card.unknownAgent')),
      h('span', { class: 'kb-card-time' },
        card.column === 'doing' ? h('span', { class: 'kb-card-live', 'aria-hidden': 'true' }) : null, cardTime(card, now()))),
    routine ? h('p', { class: 'kb-card-routine' }, icon('repeat', { size: 12 }),
      h('span', {}, card.source?.manual ? `${routine} · ${t('kb.card.manual')}` : routine)) : null,
    line.text ? h('p', { class: ['kb-card-line', `is-${line.tone}`], title: line.text }, line.text) : null,
    badges.length ? h('div', { class: 'kb-card-badges' }, badges.map((b) => h('span', { class: ['kb-badge', `is-${b.tone}`], dataset: { kind: b.kind } }, b.text))) : null,
    menuBtn);
    if (moves.length) {
      el.addEventListener('dragstart', (e) => startDrag(e, card, el, ops));
      el.addEventListener('dragend', () => endDrag());
    }
    return el;
  }

  function render() {
    if (!layer?.isOpen()) return;
    const active = globalThis.document?.activeElement;
    const fk = active?.dataset?.fk || null;
    renderToolbar();
    renderNav();
    renderNotice();
    renderBoard();
    if (fk) [...root.querySelectorAll('[data-fk]')].find((el) => el.dataset.fk === fk)?.focus?.();
  }

  // --- drag & drop ------------------------------------------------------------------------------
  function startDrag(e, card, el, ops) {
    drag = { card, canOperate: ops };
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', card.id); } catch { /* some browsers refuse */ }
    }
    el.classList.add('is-dragging');
    root.classList.add('is-dragging');
    for (const section of boardEl.querySelectorAll('.kb-col')) {
      const ok = !!moveTo(card, section.dataset.column, { canOperate: ops });
      section.classList.toggle('is-drop-ok', ok);
      section.classList.toggle('is-drop-no', !ok && section.dataset.column !== card.column);
    }
  }

  function endDrag() {
    drag = null;
    root.classList.remove('is-dragging');
    for (const el of root.querySelectorAll('.is-dragging, .is-drop-ok, .is-drop-no, .is-over')) {
      el.classList.remove('is-dragging', 'is-drop-ok', 'is-drop-no', 'is-over');
    }
  }

  // --- actions ----------------------------------------------------------------------------------
  function openTask(card) {
    // The drawer replaces the board in the history and brings it back when closed (?from=taches).
    ctx.navigate(`#/task/${encodeURIComponent(card.id)}?from=taches`, { replace: true });
  }

  function openMenu(card, anchor) {
    const moves = movesFor(card, { canOperate: canOperate(card) });
    const items = [
      { header: h('span', { class: 'kb-menu-head mono-label' }, t('kb.menu.title')) },
      ...(moves.length
        ? moves.map((m) => ({ label: moveLabel(card, m), icon: MOVE_ICONS[m.action], onSelect: () => perform(card, m) }))
        : [{ label: t(canOperate(card) ? 'kb.menu.none' : 'kb.move.noRight'), disabled: true }]),
      { separator: true },
      { label: t('kb.menu.open'), icon: 'arrowRight', onSelect: () => openTask(card) },
    ];
    children.add(ctx.overlay.menu(anchor, items, { placement: 'bottom-end', label: t('kb.card.menu', { title: card.title || t('kb.card.untitled') }) }));
  }

  async function perform(card, move, { isUndo = false } = {}) {
    if (busy.has(card.id)) return;
    if (move.action === 'cancel' && card.column === 'doing') {
      const ok = await ctx.overlay.confirm({
        title: t('kb.confirm.cancelRunning.title'), message: t('kb.confirm.cancelRunning.text'),
        confirmLabel: t('kb.confirm.cancelRunning.ok'), cancelLabel: t('kb.confirm.keep'), danger: true,
      });
      if (!ok || !layer?.isOpen()) return;
    }
    const req = moveRequest(card, move);
    if (!req) return;
    busy.add(card.id);
    st.board = withCardMoved(st.board, card.id, move.target);
    render();
    try {
      const res = await ctx.api.post(req.path, req.body);
      if (res?.id && res.state) ctx.store.applyTask?.(res);
      const count = Number(res?.count) || 1;
      const message = move.action === 'ignore' && card.state === 'awaiting_approval' ? t('kb.done.ignoreDraft')
        : move.action === 'ignore' || move.action === 'unignore' ? t(`kb.done.${move.action}`, { n: count })
          : t(`kb.done.${move.action}`);
      const undo = isUndo ? null : undoOf(card, move);
      ctx.toast.success(message, undo ? {
        action: { label: t('kb.undo'), onClick: () => perform({ ...card, column: move.target }, undo, { isUndo: true }) },
      } : {});
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      busy.delete(card.id);
      load();
    }
  }

  function setDept(id) {
    st.deptId = id;
    st.expand.clear();
    load();
  }

  function scrollToColumn(id) {
    st.activeColumn = id;
    const el = [...boardEl.querySelectorAll('.kb-col')].find((c) => c.dataset.column === id);
    el?.scrollIntoView?.({ behavior: ctx.store.get().ui?.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    renderNav();
  }

  // On a phone the column chips follow the column in view.
  let scrollRaf = 0;
  boardEl.addEventListener('scroll', () => {
    if (!st.mobile || scrollRaf) return;
    scrollRaf = (globalThis.requestAnimationFrame || setTimeout)(() => {
      scrollRaf = 0;
      // The column whose left edge is the nearest to the board's.
      const cols = [...boardEl.querySelectorAll('.kb-col')];
      const first = cols[0]?.offsetLeft || 0;
      let best = null;
      for (const c of cols) {
        const d = Math.abs(c.offsetLeft - first - boardEl.scrollLeft);
        if (!best || d < best.d) best = { d, id: c.dataset.column };
      }
      if (best && best.id !== st.activeColumn) { st.activeColumn = best.id; renderNav(); }
    });
  });

  // --- data -------------------------------------------------------------------------------------
  async function load() {
    const seq = ++loadSeq;
    clearTimeout(reloadTimer);
    layer?.setBusy?.(true);
    try {
      const res = await ctx.api.get(ctx.api.withQuery('/api/tasks/board', boardParams(st)));
      if (seq !== loadSeq || !layer?.isOpen()) return;
      st.board = res && Array.isArray(res.columns) ? res : { columns: [] };
      st.status = 'ok';
      st.error = '';
    } catch (err) {
      if (seq !== loadSeq || !layer?.isOpen()) return;
      if (err?.status === 404 && !st.board && !st.floorId && !st.deptId) st.status = 'unavailable';
      else if (err?.status === 404 && (st.floorId || st.deptId)) {
        // The floor or the department is gone (or no longer visible): back to the whole office.
        st.floorId = '';
        st.deptId = '';
        load();
        return;
      } else {
        st.status = 'error';
        st.error = err?.message || t('kb.error');
      }
    } finally {
      if (seq === loadSeq) layer?.setBusy?.(false);
    }
    render();
  }

  function scheduleReload(delay = RELOAD_DEBOUNCE_MS) {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => { if (layer?.isOpen()) load(); }, delay);
  }

  // --- open -------------------------------------------------------------------------------------
  layer = ctx.overlay.openSheet({
    title: t('kb.title'), eyebrow: t('kb.eyebrow'), tone: 'dark', className: 'kb-sheet', content: root, actions: headerActions,
  });
  children = childLayers(layer);

  offs.push(ctx.events.on('task', (task) => {
    if (isRelevant(task, st, knownCards())) scheduleReload();
  }));
  offs.push(ctx.events.on('task.removed', (d) => {
    if (d?.id && knownCards().has(d.id)) scheduleReload();
  }));
  offs.push(ctx.events.on('hello', () => scheduleReload(0)));
  offs.push(ctx.store.on('office', () => render()));

  let mq = null;
  const onMq = () => { const m = isMobile(); if (m !== st.mobile) { st.mobile = m; render(); } };
  try {
    mq = globalThis.matchMedia?.('(max-width: 767px)') || null;
    mq?.addEventListener?.('change', onMq);
  } catch { mq = null; }
  // Relative times move on.
  minuteTimer = setInterval(() => { if (!drag && !busy.size) render(); }, 60_000);
  minuteTimer.unref?.();

  layer.onClose(() => {
    for (const off of offs) { try { off?.(); } catch { /* ignore */ } }
    clearTimeout(reloadTimer);
    clearInterval(minuteTimer);
    try { mq?.removeEventListener?.('change', onMq); } catch { /* ignore */ }
    loadSeq += 1;
  });

  render();
  load();
  return layer;
}
